import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { ToastProvider } from '@/contexts/ToastContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

const MarketingPage = lazy(() => import('@/pages/MarketingPage').then(m => ({ default: m.MarketingPage })));
const SignIn = lazy(() => import('@/pages/SignIn'));
const SignUp = lazy(() => import('@/pages/SignUp'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Contact = lazy(() => import('@/pages/Contact'));
const InvitePage = lazy(() => import('@/pages/InvitePage'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Security = lazy(() => import('@/pages/Security'));
const Services = lazy(() => import('@/pages/Services'));
const FUBConfiguration = lazy(() => import('@/pages/services/FUBConfiguration'));
const DocusignDigitalReferral = lazy(() => import('@/pages/DocusignDigitalReferral'));
const Onboarding = lazy(() => import('@/pages/signup/Onboarding'));
const ConnectFUB = lazy(() => import('@/pages/signup/ConnectFUB'));
const SelectPlan = lazy(() => import('@/pages/signup/SelectPlan'));
const ChooseAutomations = lazy(() => import('@/pages/signup/ChooseAutomations'));
const SignupSuccess = lazy(() => import('@/pages/signup/Success'));
const OnboardingComplete = lazy(() => import('@/pages/signup/OnboardingComplete'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const Catalog = lazy(() => import('@/pages/Catalog'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const ContactSyncLanding = lazy(() => import('@/pages/ContactSyncLanding'));
const AdminRouter = lazy(() => import('@/pages/admin/AdminRouter').then(m => ({ default: m.AdminRouter })));
const ClientPortal = lazy(() => import('@/pages/client/ClientPortal').then(m => ({ default: m.ClientPortal })));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Router>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<MarketingPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/fub-configuration" element={<FUBConfiguration />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/sign-in" element={<Navigate to="/signin" replace />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/sign-up" element={<Navigate to="/signup" replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/signup/onboarding" element={<Onboarding />} />
              <Route path="/signup/connect-fub" element={<ConnectFUB />} />
              <Route path="/signup/select-plan" element={<SelectPlan />} />
              <Route path="/signup/success" element={<SignupSuccess />} />
              <Route path="/signup/choose-automations" element={<ChooseAutomations />} />
              <Route path="/signup/complete" element={<OnboardingComplete />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact-sync" element={<ContactSyncLanding />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/security" element={<Security />} />
              <Route
                path="/docusign/digital-referral"
                element={
                  <ProtectedRoute>
                    <DocusignDigitalReferral />
                  </ProtectedRoute>
                }
              />
              <Route path="/admin/*" element={<AdminRouter />} />
              <Route path="/portal/*" element={<ClientPortal />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Toaster />
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
